import { useEffect, useState, type FC } from 'react';
import { SearchInput, Select, useDebouncedValue } from '@/ui';

import type { ActivityFilter, KindFilter } from './lib/searchParams';

export interface ProductsFiltersProps {
  search: string;
  kind: KindFilter;
  /** Выбранная группа номенклатуры; '' - все. */
  group: string;
  /** Группы, в которых есть позиции. */
  groups: ReadonlyArray<string>;
  activity: ActivityFilter;
  onSearchChange: (value: string) => void;
  onKindChange: (value: KindFilter) => void;
  onGroupChange: (value: string) => void;
  onActivityChange: (value: ActivityFilter) => void;
}

/**
 * Панель фильтров справочника: поиск по коду и названию, тип культуры, группа
 * номенклатуры 1С, доступность.
 */
export const ProductsFilters: FC<ProductsFiltersProps> = ({
  search,
  kind,
  group,
  groups,
  activity,
  onSearchChange,
  onKindChange,
  onGroupChange,
  onActivityChange,
}) => {
  // Поле ввода живёт локально, в URL уезжает отложенное значение: иначе каждый
  // символ - и запись в историю браузера, и запрос в API.
  const [draft, setDraft] = useState(search);
  const debounced = useDebouncedValue(draft, 300);

  useEffect(() => {
    if (debounced !== search) onSearchChange(debounced);
    // onSearchChange меняется на каждый рендер маршрута; следим только за значением.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  // Внешний сброс (навигация «назад», очистка) должен вернуться в поле.
  useEffect(() => {
    setDraft((current) => (current === search ? current : search));
  }, [search]);

  // Группа из адреса может не попасть в список (сменили доступность) - её всё
  // равно показываем, иначе поле выбора соврёт.
  const groupOptions =
    group !== '' && !groups.includes(group) ? [group, ...groups] : groups;

  return (
    <div className="mb-3.5 flex flex-wrap items-center gap-2 rounded-lg border border-ink-200 bg-white px-3.5 py-3">
      <SearchInput
        className="min-w-[220px] flex-1"
        placeholder="Поиск по артикулу или наименованию..."
        aria-label="Поиск по справочнику"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />

      <Select
        aria-label="Тип культуры"
        value={kind}
        onChange={(event) => onKindChange(event.target.value as KindFilter)}
      >
        <option value="all">Все типы</option>
        <option value="Starter">Закваски</option>
        <option value="Enzyme">Ферменты</option>
        <option value="Unspecified">Тип не определён</option>
      </Select>

      <Select
        aria-label="Группа номенклатуры"
        value={group}
        onChange={(event) => onGroupChange(event.target.value)}
      >
        <option value="">Все группы</option>
        {groupOptions.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </Select>

      <Select
        aria-label="Доступность позиции"
        value={activity}
        onChange={(event) =>
          onActivityChange(event.target.value as ActivityFilter)
        }
      >
        <option value="active">Только активные</option>
        <option value="inactive">Только снятые</option>
        <option value="all">Все позиции</option>
      </Select>
    </div>
  );
};
